import { ReactNode } from 'react';
import {
  Check,
  Eye,
  EyeOff,
  Store,
  Lock,
  MessageSquareWarning,
  PencilRuler,
  Star,
} from 'lucide-react';
import { PiPuzzlePieceFill } from 'react-icons/pi';
import OnefinityOfficialIcon from '@/assets/onefinity_officials_badge.png';
import RewardCoin from '@/components/Icons/RewardCoin';

export type IconLegendCategory =
  | 'badge'
  | 'project-status'
  | 'project-attribute'
  | 'rewards';

export interface IconLegendEntry {
  key: string;
  name: string;
  description: string;
  category: IconLegendCategory;
  render: (size?: number) => ReactNode;
}

const renderOnefinityOfficial = (size = 32) => (
  <img
    src={OnefinityOfficialIcon}
    alt="Onefinity official badge"
    style={{ width: size, height: size }}
  />
);

const renderPartner = (size = 32) => (
  <div
    className="rounded-full flex items-center justify-center"
    style={{ width: size, height: size, backgroundColor: '#000' }}
  >
    <Star
      className="text-white"
      fill="white"
      style={{ width: size * 0.5, height: size * 0.5 }}
    />
  </div>
);

function makeStatusBadgeRender(
  Icon: typeof Lock,
  bg: string
): (size?: number) => ReactNode {
  return function statusBadgeRender(size = 32) {
    return (
      <div
        className="rounded-md flex items-center justify-center text-white"
        style={{ width: size, height: size, backgroundColor: bg }}
      >
        <Icon style={{ width: size * 0.55, height: size * 0.55 }} />
      </div>
    );
  };
}

export const ICON_LEGEND: IconLegendEntry[] = [
  {
    key: 'dimensional-material',
    name: 'Big Box Store Lumber',
    description:
      "This project links to lumber sold at a big box store such as Home Depot or Lowe's.",
    category: 'project-attribute',
    render: (size = 32) => (
      <Store
        className="text-violet-500"
        style={{ width: size, height: size }}
      />
    ),
  },
  {
    key: 'multi-piece-project',
    name: 'Multi-piece Project',
    description:
      'This is a combination of multiple single-piece projects (a project group).',
    category: 'project-attribute',
    render: (size = 32) => (
      <PiPuzzlePieceFill
        className="text-indigo-600"
        style={{ width: size, height: size }}
      />
    ),
  },
  {
    key: 'onefinity-official',
    name: 'Onefinity Official',
    description: 'This account belongs to a Onefinity official person.',
    category: 'badge',
    render: renderOnefinityOfficial,
  },
  {
    key: 'trusted-member',
    name: 'Trusted Member',
    description: 'This account is a trusted member of the Onefinity community.',
    category: 'badge',
    render: (size = 32) => (
      <div
        className="rounded-full bg-blue-500 flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <Check
          className="text-white"
          strokeWidth={3}
          style={{ width: size * 0.6, height: size * 0.6 }}
        />
      </div>
    ),
  },
  {
    key: 'partner',
    name: 'Partner Badge',
    description:
      'This account belongs to a Onefinity partner. Partners are differentiated by different colors.',
    category: 'badge',
    render: renderPartner,
  },
  {
    key: 'project-public',
    name: 'Public Project',
    description: 'Project is public and visible to everyone.',
    category: 'project-status',
    render: makeStatusBadgeRender(Eye, '#22c55e'),
  },
  {
    key: 'project-private',
    name: 'Private Project',
    description: 'Project is private (pending review).',
    category: 'project-status',
    render: makeStatusBadgeRender(Lock, '#f97316'),
  },
  {
    key: 'project-draft',
    name: 'Draft Project',
    description: 'Project is in draft.',
    category: 'project-status',
    render: makeStatusBadgeRender(PencilRuler, '#eab308'),
  },
  {
    key: 'project-unlisted',
    name: 'Unlisted Project',
    description: 'Project is unlisted — accessible only with a direct link.',
    category: 'project-status',
    render: makeStatusBadgeRender(EyeOff, '#000000'),
  },
  {
    key: 'project-rejected',
    name: 'Post Rejected',
    description:
      'Post is rejected by an admin. Check the comments and make the required edits.',
    category: 'project-status',
    render: makeStatusBadgeRender(MessageSquareWarning, '#ef4444'),
  },
  {
    key: 'coin-bronze',
    name: 'Bronze Coin',
    description: 'Bronze tier in the points and redemption system.',
    category: 'rewards',
    render: (size = 36) => (
      <RewardCoin coinType="bronze" width={size} height={size} />
    ),
  },
  {
    key: 'coin-silver',
    name: 'Silver Coin',
    description: 'Silver tier in the points and redemption system.',
    category: 'rewards',
    render: (size = 36) => (
      <RewardCoin coinType="silver" width={size} height={size} />
    ),
  },
  {
    key: 'coin-gold',
    name: 'Gold Coin',
    description: 'Gold tier in the points and redemption system.',
    category: 'rewards',
    render: (size = 36) => (
      <RewardCoin coinType="gold" width={size} height={size} />
    ),
  },
  {
    key: 'coin-platinum',
    name: 'Platinum Coin',
    description: 'Platinum tier in the points and redemption system.',
    category: 'rewards',
    render: (size = 36) => (
      <RewardCoin coinType="platinum" width={size} height={size} />
    ),
  },
];

export const ICON_LEGEND_BY_KEY: Record<string, IconLegendEntry> =
  ICON_LEGEND.reduce(
    (acc, entry) => {
      acc[entry.key] = entry;
      return acc;
    },
    {} as Record<string, IconLegendEntry>
  );

export const CATEGORY_LABELS: Record<IconLegendCategory, string> = {
  badge: 'Account Badges',
  'project-status': 'Project Status',
  'project-attribute': 'Project Attributes',
  rewards: 'Rewards & Tiers',
};
